import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import EventStatistics from "../../features/events/EventStatistics";
import imageService from "../../services/imageService";
import UMTransactionList from "../../features/university-manager/UMTransactionList";
import DemographicList from "./DemographicList";
import umService from "../../services/university-admin/umService";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";

// ─── Label maps ──────────────────────────────────────────────────────────────

const PARTICIPATION_TYPE_CONFIG = {
  ONLINE: { icon: "fa-wifi", color: "info" },
  PHYSICAL: { icon: "fa-map-marker-alt", color: "danger" },
};

const PARTICIPATION_SCOPE_CONFIG = {
  CLUB_ONLY: { icon: "fa-lock", color: "secondary" },
  CAMPUS_ONLY: { icon: "fa-building", color: "primary" },
  UNIVERSITY_ONLY: { icon: "fa-university", color: "info" },
  COUNTRY_ONLY: { icon: "fa-flag", color: "success" },
  PUBLIC: { icon: "fa-globe", color: "warning" },
};

const EVENT_STATUS_CONFIG = {
  UPCOMING: { color: "primary", icon: "clock" },
  ONGOING: { color: "success", icon: "play" },
  COMPLETED: { color: "info", icon: "check" },
  CANCELLED: { color: "danger", icon: "times" },
  POSTPONED: { color: "warning", icon: "pause" },
  PENDING: { color: "warning", icon: "hourglass-half" },
  PAST: { color: "secondary", icon: "history" },
  REJECTED: { color: "danger", icon: "times-circle" },
};

const APPROVAL_STATUS_CONFIG = {
  PENDING: { color: "warning", icon: "fa-clock" },
  APPROVED: { color: "success", icon: "fa-check-circle" },
  REJECTED: { color: "danger", icon: "fa-times-circle" },
};

const parseAddressString = (addressStr) => {
  if (!addressStr || typeof addressStr !== "string") return null;
  const extract = (key) => {
    const match = addressStr.match(new RegExp(`${key}=([^,)]+)`));
    return match ? match[1].trim() : null;
  };
  const lat = extract("latitude");
  const lng = extract("longitude");
  const descEn = extract("descriptionEn");
  const descLocal = extract("descriptionLocal");
  if (!lat && !lng && !descEn && !descLocal) return null;
  return { latitude: lat, longitude: lng, descriptionEn: descEn, descriptionLocal: descLocal };
};

// ─── Badge components ─────────────────────────────────────────────────────────

const TypeBadge = ({ type }) => {
  const { t } = useTranslation("events");
  const cfg = PARTICIPATION_TYPE_CONFIG[type] || { icon: "fa-question", color: "secondary" };
  const label = type ? t(`participationType.${type}`, { defaultValue: type }) : type;
  return (
    <span className={`badge bg-${cfg.color} bg-opacity-10 text-${cfg.color}`}>
      <i className={`fas ${cfg.icon} me-1`}></i>{label}
    </span>
  );
};

const ScopeBadge = ({ scope }) => {
  const { t } = useTranslation("events");
  const cfg = PARTICIPATION_SCOPE_CONFIG[scope] || { icon: "fa-users", color: "secondary" };
  const label = scope ? t(`participationScope.${scope}`, { defaultValue: scope?.replace(/_/g, " ") }) : scope;
  return (
    <span className={`badge bg-${cfg.color} bg-opacity-10 text-${cfg.color}`}>
      <i className={`fas ${cfg.icon} me-1`}></i>{label}
    </span>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * Shared event detail modal used by both admin/manager panel and university manager panel.
 *
 * Props:
 *   event             - event object (required)
 *   onClose           - called when modal is dismissed (required)
 *   onEdit            - if provided, shows "Edit Event" footer button
 *   onCancel          - if provided, shows "Cancel Event" footer button
 *   onDelete          - if provided, shows "Delete Event" footer button
 *   onManageTicketAttendants - if provided, shows "Manage Ticket Attendants" footer button
 *   statisticsData    - if provided, shows EventStatistics modal overlay
 *   onViewStatistics  - callback to open statistics; shows button when event.statistics exists
 *   onCloseStatistics - callback to close statistics overlay
 */
const EventDetailModal = ({ event, onClose, onEdit, onCancel, onDelete, onManageTicketAttendants, statisticsData, onViewStatistics, onCloseStatistics }) => {
  const { t, i18n } = useTranslation(["events", "common"]);
  const { can } = useUniAdminAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [eventStats, setEventStats] = useState(null);
  const [eventStatsLoading, setEventStatsLoading] = useState(false);

  useEffect(() => {
    if (activeTab !== "exports" || !event?.id || eventStats || !can(PERMISSIONS.event.statistics)) return;
    let cancelled = false;
    setEventStatsLoading(true);
    umService.getEventStatistics(event.id).then((res) => {
      if (!cancelled) setEventStats(res.success ? res.data : null);
    }).finally(() => {
      if (!cancelled) setEventStatsLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, event?.id]);

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString(i18n.language === "tr" ? "tr-TR" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!event) return null;

  // Normalise field names that differ between admin and UM data shapes
  const clubDisplay = event.clubName || event.clubNameEn;
  const campusDisplay = event.campusName || event.campusNameEn;
  const universityDisplay = event.universityName || event.universityNameEn;

  // Resolve images — prefer the explicit URL fields returned by the detail endpoint
  const imageEn = event.imageUrlEn || imageService.getImageUrl(event.imageEnId);
  const imageLocal = event.imageUrlLocal || event.imageUrlTr || imageService.getImageUrl(event.imageLocalId);
  const imageFallback = event.imageUrl || imageService.getImageUrl(event.imageId);

  const eventStatusCfg = EVENT_STATUS_CONFIG[event.eventStatus] || { color: "secondary", icon: "question" };
  const approvalStatusCfg = event.approvalStatus ? APPROVAL_STATUS_CONFIG[event.approvalStatus] : null;

  const addrObj =
    typeof event.address === "object" ? event.address : parseAddressString(event.address);

  return (
    <>
      <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-xl">
          <div className="modal-content">

            {/* Header */}
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-calendar-alt me-2"></i>
                {t("events:eventDetails")}
              </h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>

            {/* Body */}
            <div className="modal-body">
              <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                  <button className={`nav-link ${activeTab === "overview" ? "active fw-bold" : ""}`} onClick={() => setActiveTab("overview")}>{t("common:tabs.overview")}</button>
                </li>
                <li className="nav-item">
                  <button className={`nav-link ${activeTab === "financials" ? "active fw-bold" : ""}`} onClick={() => setActiveTab("financials")}>{t("common:tabs.financials")}</button>
                </li>
                <li className="nav-item">
                  <button className={`nav-link ${activeTab === "exports" ? "active fw-bold" : ""}`} onClick={() => setActiveTab("exports")}>{t("common:tabs.exports")}</button>
                </li>
              </ul>

              {activeTab === "overview" && (
                <div className="row">

                  {/* ── Left column ─────────────────────────────────────── */}
                <div className="col-md-8">

                  {/* Title / badges / club row */}
                  <div className="d-flex align-items-start mb-4">
                    <div
                      className="event-icon bg-primary rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                      style={{ width: "60px", height: "60px" }}
                    >
                      <i className="fas fa-calendar-alt text-white fs-4"></i>
                    </div>
                    <div className="flex-grow-1">
                      <h4 className="fw-bold text-dark mb-1">{event.titleEn || event.title}</h4>
                      {event.titleLocal && <p className="text-muted mb-2">{event.titleLocal}</p>}
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        {/* Event status */}
                        <span className={`badge bg-${eventStatusCfg.color} bg-opacity-10 text-${eventStatusCfg.color}`}>
                          <i className={`fas fa-${eventStatusCfg.icon} me-1`}></i>
                          {t(`events:status.${event.eventStatus}`, { defaultValue: event.eventStatus })}
                        </span>
                        {/* Active/inactive — admin only */}
                        {typeof event.status === "boolean" && (
                          <span className={`badge ${event.status ? "bg-success" : "bg-danger"} bg-opacity-10 text-${event.status ? "success" : "danger"}`}>
                            <i className={`fas ${event.status ? "fa-check" : "fa-times"} me-1`}></i>
                            {event.status ? t("common:status.active") : t("common:status.inactive")}
                          </span>
                        )}
                        {/* Approval — UM only */}
                        {approvalStatusCfg && (
                          <span className={`badge bg-${approvalStatusCfg.color} bg-opacity-10 text-${approvalStatusCfg.color}`}>
                            <i className={`fas ${approvalStatusCfg.icon} me-1`}></i>
                            {t(`events:approvalStatus.${event.approvalStatus}`, { defaultValue: event.approvalStatus })}
                          </span>
                        )}
                        <ScopeBadge scope={event.participationScope} />
                        <TypeBadge type={event.participationType} />
                        {typeof event.isUniversityEvent === "boolean" && (
                          <span className="badge bg-secondary bg-opacity-10 text-secondary">
                            <i className={`fas ${event.isUniversityEvent ? "fa-university" : "fa-users"} me-1`}></i>
                            {event.isUniversityEvent ? t("events:detail.universityEvent") : t("events:detail.clubEvent")}
                          </span>
                        )}
                      </div>
                      {(clubDisplay || campusDisplay || universityDisplay) && (
                        <div className="text-muted small">
                          <i className="fas fa-users me-1"></i>
                          {[clubDisplay, campusDisplay, universityDisplay].filter(Boolean).join(" • ")}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ID & Price */}
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <label className="form-label fw-bold text-muted small">{t("events:detail.eventId").toUpperCase()}</label>
                      <p className="text-dark fs-5 mb-0">{event.id}</p>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold text-muted small">{t("events:detail.price").toUpperCase()}</label>
                      <p className="text-dark fs-5 mb-0">
                        {event.price > 0 ? `₺${event.price}` : t("events:detail.free")}
                      </p>
                    </div>
                  </div>

                  {/* Participation */}
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <label className="form-label fw-bold text-muted small">{t("events:detail.participationScope").toUpperCase()}</label>
                      <div><ScopeBadge scope={event.participationScope} /></div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold text-muted small">{t("events:detail.participationType").toUpperCase()}</label>
                      <div><TypeBadge type={event.participationType} /></div>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <label className="form-label fw-bold text-muted small">{t("events:detail.startDate").toUpperCase()}</label>
                      <p className="text-dark mb-0">
                        <i className="fas fa-calendar me-2 text-info"></i>
                        {formatDate(event.startDate)}
                      </p>
                    </div>
                    {event.endDate && (
                      <div className="col-md-6">
                        <label className="form-label fw-bold text-muted small">{t("events:detail.endDate").toUpperCase()}</label>
                        <p className="text-dark mb-0">
                          <i className="fas fa-calendar-check me-2 text-success"></i>
                          {formatDate(event.endDate)}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Address */}
                  {event.address && (
                    <div className="row mb-4">
                      <div className="col-md-12">
                        <label className="form-label fw-bold text-muted small">{t("events:detail.address").toUpperCase()}</label>
                        <div className="card border-0 bg-light">
                          <div className="card-body py-3">
                            {addrObj ? (
                              <div className="row g-2">
                                {addrObj.descriptionEn && (
                                  <div className="col-md-6">
                                    <small className="text-muted d-block">{t("events:detail.addressDescEn")}</small>
                                    <span className="fw-medium">{addrObj.descriptionEn}</span>
                                  </div>
                                )}
                                {addrObj.descriptionLocal && (
                                  <div className="col-md-6">
                                    <small className="text-muted d-block">{t("events:detail.addressDescLocal")}</small>
                                    <span className="fw-medium">{addrObj.descriptionLocal}</span>
                                  </div>
                                )}
                                {(addrObj.latitude || addrObj.longitude) && (
                                  <div className="col-12 mt-1">
                                    <small className="text-muted">
                                      <i className="fas fa-map-marker-alt me-1"></i>
                                      {addrObj.latitude}, {addrObj.longitude}
                                    </small>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-muted">
                                <i className="fas fa-map-marker-alt me-2"></i>
                                {event.address}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Descriptions */}
                  {event.descriptionEn && (
                    <div className="row mb-4">
                      <div className="col-12">
                        <label className="form-label fw-bold text-muted small">{t("events:detail.descriptionEn").toUpperCase()}</label>
                        <p className="text-dark mb-0">{event.descriptionEn}</p>
                      </div>
                    </div>
                  )}
                  {event.descriptionLocal && (
                    <div className="row mb-4">
                      <div className="col-12">
                        <label className="form-label fw-bold text-muted small">{t("events:detail.descriptionLocal").toUpperCase()}</label>
                        <p className="text-dark mb-0">{event.descriptionLocal}</p>
                      </div>
                    </div>
                  )}

                  {/* Images */}
                  {(imageEn || imageLocal || imageFallback) && (
                    <div className="row mb-4">
                      {imageEn && (
                        <div className={`col-md-${imageLocal ? "6" : "12"} mb-3 mb-md-0`}>
                          <label className="form-label fw-bold text-muted small">{t("events:detail.imageEn").toUpperCase()}</label>
                          <div className="border rounded overflow-hidden">
                            <img
                              src={imageEn}
                              alt="Event EN"
                              className="img-fluid w-100"
                              style={{ maxHeight: "220px", objectFit: "cover" }}
                            />
                          </div>
                        </div>
                      )}
                      {imageLocal && (
                        <div className={`col-md-${imageEn ? "6" : "12"}`}>
                          <label className="form-label fw-bold text-muted small">{t("events:detail.imageLocal").toUpperCase()}</label>
                          <div className="border rounded overflow-hidden">
                            <img
                              src={imageLocal}
                              alt="Event Local"
                              className="img-fluid w-100"
                              style={{ maxHeight: "220px", objectFit: "cover" }}
                            />
                          </div>
                        </div>
                      )}
                      {!imageEn && !imageLocal && imageFallback && (
                        <div className="col-12">
                          <label className="form-label fw-bold text-muted small">{t("events:detail.image").toUpperCase()}</label>
                          <div className="border rounded overflow-hidden">
                            <img
                              src={imageFallback}
                              alt="Event"
                              className="img-fluid w-100"
                              style={{ maxHeight: "220px", objectFit: "cover" }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Rejection reason — UM only */}
                  {(event.rejectionMessage || event.rejectionReason) && (
                    <div className="row mb-4">
                      <div className="col-12">
                        <label className="form-label fw-bold text-muted small">{t("events:detail.rejectionReason").toUpperCase()}</label>
                        <div className="alert alert-danger py-2 mb-0">
                          <i className="fas fa-times-circle me-2"></i>
                          {event.rejectionMessage || event.rejectionReason}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Created at */}
                  {event.createdAt && (
                    <div className="row">
                      <div className="col-md-6">
                        <label className="form-label fw-bold text-muted small">{t("events:detail.created").toUpperCase()}</label>
                        <p className="text-muted small mb-0">{formatDate(event.createdAt)}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Right column: stats panel ────────────────────────── */}
                <div className="col-md-4">
                  <div className="card border-0 bg-light h-100">
                    <div className="card-body">
                      <h6 className="fw-bold mb-4">
                        <i className="fas fa-chart-bar me-2 text-primary"></i>
                        {t("events:detail.statistics")}
                      </h6>

                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-success rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: "40px", height: "40px" }}>
                          <i className="fas fa-user-check text-white"></i>
                        </div>
                        <div>
                          <div className="fw-bold fs-4">{event.joinCount || 0}</div>
                          <small className="text-muted">{t("events:detail.registered")}</small>
                        </div>
                      </div>

                      <div className="d-flex align-items-center mb-4">
                        <div className="bg-info rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: "40px", height: "40px" }}>
                          <i className="fas fa-users text-white"></i>
                        </div>
                        <div>
                          <div className="fw-bold fs-4">{event.actualParticipantCount || 0}</div>
                          <small className="text-muted">{t("events:detail.attended")}</small>
                        </div>
                      </div>

                      {/* Event type breakdown */}
                      <div className="border-top pt-3 mb-3">
                        <h6 className="fw-bold mb-3">{t("events:detail.eventType")}</h6>
                        <div className="d-flex align-items-center mb-2">
                          <TypeBadge type={event.participationType} />
                        </div>
                        <div className="d-flex align-items-center mb-2">
                          <ScopeBadge scope={event.participationScope} />
                        </div>
                        {event.price > 0 && (
                          <div className="d-flex align-items-center mt-1">
                            <i className="fas fa-lira-sign text-warning me-2"></i>
                            <span className="small">₺{event.price}</span>
                          </div>
                        )}
                      </div>

                      {/* Attendance rate */}
                      {event.joinCount > 0 && (
                        <div className="border-top pt-3 mb-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="small text-muted">{t("events:detail.attendanceRate")}</span>
                            <span className="fw-bold text-primary">
                              {Math.round((event.actualParticipantCount / event.joinCount) * 100)}%
                            </span>
                          </div>
                          <div className="progress mt-1" style={{ height: "6px" }}>
                            <div
                              className="progress-bar bg-primary"
                              style={{ width: `${(event.actualParticipantCount / event.joinCount) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      )}

                      {/* Quota / attendee capacity — UM only */}
                      {(event.quota != null || event.attendeeCount != null) && (
                        <div className="border-top pt-3 mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="small text-muted">{t("events:detail.attendeeCount")}</span>
                            <span className="fw-bold">
                              {event.attendeeCount ?? 0}{event.quota != null ? ` / ${event.quota}` : ""}
                            </span>
                          </div>
                          {event.quota != null && event.attendeeCount != null && (
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="small text-muted">{t("events:detail.spotsRemaining")}</span>
                              <span className="fw-bold">{Math.max(0, event.quota - event.attendeeCount)}</span>
                            </div>
                          )}
                          {event.isFull && (
                            <span className="badge bg-danger bg-opacity-10 text-danger mt-2">
                              <i className="fas fa-exclamation-circle me-1"></i>
                              {t("events:detail.full")}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Approval status — UM only */}
                      {event.approvalStatus && (
                        <div className="border-top pt-3 mb-3">
                          <h6 className="fw-bold mb-2">{t("events:detail.approval")}</h6>
                          {approvalStatusCfg && (
                            <span className={`badge bg-${approvalStatusCfg.color} bg-opacity-10 text-${approvalStatusCfg.color}`}>
                              <i className={`fas ${approvalStatusCfg.icon} me-1`}></i>
                              {t(`events:approvalStatus.${event.approvalStatus}`, { defaultValue: event.approvalStatus })}
                            </span>
                          )}
                        </div>
                      )}

                      {/* View detailed statistics — admin only */}
                      {onViewStatistics && (
                        <div className="border-top pt-3">
                          <button className="btn btn-outline-primary btn-sm w-100" onClick={() => onViewStatistics(event)}>
                            <i className="fas fa-chart-bar me-2"></i>
                            {t("events:detail.viewDetailedStatistics")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
              )}

              {activeTab === "financials" && (
                <UMTransactionList eventId={event.id} title="Event Financials" />
              )}

              {activeTab === "exports" && (
                <div className="p-4">
                  {can(PERMISSIONS.event.statistics) && (
                    <div className="mb-4 pb-4 border-bottom">
                      <h6 className="fw-bold mb-3">
                        <i className="fas fa-chart-bar me-2 text-primary"></i>
                        {t("common:statisticsPanel.title")}
                      </h6>
                      {eventStatsLoading ? (
                        <div className="text-center py-3">
                          <div className="spinner-border spinner-border-sm text-primary"></div>
                        </div>
                      ) : eventStats ? (
                        <div className="row">
                          <div className="col-12 mb-3">
                            <div className="row text-center">
                              <div className="col-md-4 mb-2">
                                <div className="fw-bold fs-4 text-success">{eventStats.totalTicketOwners ?? 0}</div>
                                <small className="text-muted">{t("common:statisticsPanel.totalTicketOwners")}</small>
                              </div>
                              <div className="col-md-4 mb-2">
                                <div className="fw-bold fs-4 text-info">{eventStats.totalAttended ?? 0}</div>
                                <small className="text-muted">{t("common:statisticsPanel.totalAttended")}</small>
                              </div>
                              <div className="col-md-4 mb-2">
                                <div className="fw-bold fs-4 text-primary">{eventStats.organizingClubMemberAttendees ?? 0}</div>
                                <small className="text-muted">{t("common:statisticsPanel.organizingClubMemberAttendees")}</small>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <DemographicList title={t("common:statisticsPanel.byGrade")} icon="fa-graduation-cap" items={eventStats.byGrade} emptyLabel={t("common:statisticsPanel.noBreakdownData")} translateLabel={(label) => t(`users:grade.${label}`, { defaultValue: label })} />
                            <DemographicList title={t("common:statisticsPanel.byDepartment")} icon="fa-building" items={eventStats.byDepartment} emptyLabel={t("common:statisticsPanel.noBreakdownData")} />
                          </div>
                          <div className="col-md-6">
                            <DemographicList title={t("common:statisticsPanel.byGender")} icon="fa-venus-mars" items={eventStats.byGender} emptyLabel={t("common:statisticsPanel.noBreakdownData")} translateLabel={(label) => t(`users:gender.${label}`, { defaultValue: label })} />
                            <DemographicList title={t("common:statisticsPanel.byHomeUniversity")} icon="fa-university" items={eventStats.byHomeUniversity} emptyLabel={t("common:statisticsPanel.noBreakdownData")} />
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted small">{t("common:statisticsPanel.noData")}</p>
                      )}
                    </div>
                  )}
                  <h6 className="fw-bold mb-4">{t("common:exports.eventTitle")}</h6>
                  <div className="d-flex flex-wrap gap-3">
                    <button className="btn btn-outline-danger" onClick={() => umService.exportEventStatistics(event.id)}>
                      <i className="fas fa-file-pdf me-2"></i> {t("common:exports.statisticsPdf")}
                    </button>
                    <button className="btn btn-outline-success" onClick={() => umService.exportEventParticipants(event.id, 'EXCEL')}>
                      <i className="fas fa-file-excel me-2"></i> {t("common:exports.participantsExcel")}
                    </button>
                    <button className="btn btn-outline-info" onClick={() => umService.exportEventParticipants(event.id, 'CSV')}>
                      <i className="fas fa-file-csv me-2"></i> {t("common:exports.participantsCsv")}
                    </button>
                    <button className="btn btn-outline-success" onClick={() => umService.exportEventTicketOwners(event.id, 'EXCEL')}>
                      <i className="fas fa-file-excel me-2"></i> {t("common:exports.ticketOwnersExcel")}
                    </button>
                    <button className="btn btn-outline-info" onClick={() => umService.exportEventTicketOwners(event.id, 'CSV')}>
                      <i className="fas fa-file-csv me-2"></i> {t("common:exports.ticketOwnersCsv")}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer">
              {onManageTicketAttendants && (
                <button type="button" className="btn btn-outline-secondary" onClick={() => onManageTicketAttendants(event)}>
                  <i className="fas fa-id-badge me-2"></i>
                  {t("events:manageTicketAttendants")}
                </button>
              )}
              {onEdit && (
                <button type="button" className="btn btn-outline-primary" onClick={() => onEdit(event)}>
                  <i className="fas fa-edit me-2"></i>
                  {t("events:editEvent")}
                </button>
              )}
              {onCancel && event.eventStatus !== "CANCELLED" && event.eventStatus !== "PAST" && (
                <button type="button" className="btn btn-outline-warning" onClick={() => onCancel(event)}>
                  <i className="fas fa-ban me-2"></i>
                  {t("events:cancelEvent")}
                </button>
              )}
              {onDelete && (
                <button type="button" className="btn btn-outline-danger" onClick={() => onDelete(event)}>
                  <i className="fas fa-trash me-2"></i>
                  {t("events:deleteEvent")}
                </button>
              )}
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                {t("common:buttons.close")}
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* Statistics overlay */}
      {statisticsData && onCloseStatistics && (
        <EventStatistics statistics={statisticsData} onClose={onCloseStatistics} />
      )}
    </>
  );
};

export { TypeBadge, ScopeBadge, PARTICIPATION_TYPE_CONFIG, PARTICIPATION_SCOPE_CONFIG };
export default EventDetailModal;
